import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import BookCard from "./BookCard";
import { CATEGORIES } from "../mock";
import { useLang } from "../context/LanguageContext";
import { useCatalog } from "../context/CatalogContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

/**
 * The catalogue, searched and filtered.
 *
 * One component behind /browse, /author/:name and /narrator/:name. A narrator
 * page is the catalogue with a narrator filter applied — the same thing Audible
 * means by /search?searchNarrator= — so it should carry the same sorting,
 * length and rating controls rather than being a flat grid that happens to
 * share a heading.
 *
 * `scope` pins a filter that the reader cannot clear: on /narrator/Diana%20Reyes
 * the narrator is the page, not a choice. Everything else stays in the URL, so
 * a filtered view can be linked and survives a refresh.
 */
export default function CatalogBrowser({ scope = null, header = null }) {
  const { t } = useLang();
  const { books: BOOKS, loading } = useCatalog();
  const [searchParams, setSearchParams] = useSearchParams();

  const read = (key, fallback = "all") => searchParams.get(key) || fallback;
  const [query, setQuery] = useState(read("q", ""));

  // The text box owns its value while being typed; the URL catches up behind a
  // debounce. Writing a history entry per keystroke meant Back walked the search
  // term backwards one letter at a time instead of leaving the page.
  const debounce = useRef(null);
  useEffect(() => {
    setQuery(searchParams.get("q") || "");
  }, [searchParams]);

  const setParam = (key, value, { replace = true } = {}) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace });
  };

  const onQueryChange = (value) => {
    setQuery(value);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setParam("q", value.trim()), 300);
  };

  const category = read("cat");
  const sort = read("sort", "popular");
  const length = read("len");
  const minRating = read("rating");
  // Free-text people filters, so /browse?narrator=Diana+Reyes works the way the
  // pretty /narrator/... route does.
  const author = scope?.author ?? searchParams.get("author") ?? "";
  const narrator = scope?.narrator ?? searchParams.get("narrator") ?? "";

  const clearable = [
    category !== "all", length !== "all", minRating !== "all", query.trim(),
    !scope?.author && author, !scope?.narrator && narrator,
  ].some(Boolean);

  const clearFilters = () => {
    const next = new URLSearchParams();
    // A pinned scope survives "clear": it is the page, not a filter.
    if (!scope) {
      if (searchParams.get("author") && scope?.author) next.set("author", author);
    }
    setQuery("");
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    let list = [...BOOKS];
    if (author) list = list.filter((b) => b.author === author);
    if (narrator) list = list.filter((b) => b.narrator === narrator);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (b) =>
          b.title_en.toLowerCase().includes(q) ||
          b.author.toLowerCase().includes(q) ||
          b.narrator.toLowerCase().includes(q)
      );
    }
    if (category !== "all") list = list.filter((b) => b.category === category);
    if (minRating !== "all") {
      list = list.filter((b) => (b.ratingCount > 0 ? b.rating : 0) >= parseFloat(minRating));
    }
    if (length !== "all") {
      list = list.filter((b) => {
        const h = (b.duration || 0) / 3600;
        if (length === "short") return h < 6;
        if (length === "medium") return h >= 6 && h <= 12;
        return h > 12;
      });
    }
    switch (sort) {
      case "newest":
        list.sort((a, b) => (b.releaseYear || 0) - (a.releaseYear || 0));
        break;
      case "rating":
        list.sort((a, b) => b.rating - a.rating);
        break;
      case "price_low":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price_high":
        list.sort((a, b) => b.price - a.price);
        break;
      case "longest":
        list.sort((a, b) => (b.duration || 0) - (a.duration || 0));
        break;
      default:
        list.sort((a, b) => b.ratingCount - a.ratingCount);
    }
    return list;
  }, [BOOKS, query, category, sort, length, minRating, author, narrator]);

  const chips = [
    !scope?.author && author && { key: "author", label: `Written by ${author}` },
    !scope?.narrator && narrator && { key: "narrator", label: `Narrated by ${narrator}` },
  ].filter(Boolean);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-float-up">
      {header || (
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{t("catalog")}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {filtered.length} {t("results")}
          </p>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <label htmlFor="cat-search" className="sr-only">{t("search_placeholder")}</label>
          <input
            id="cat-search"
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={scope ? "Search within these audiobooks…" : t("search_placeholder")}
            className="w-full h-11 pl-11 pr-4 rounded-xl bg-secondary border border-border text-sm outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all"
          />
        </div>
        <Select value={category} onValueChange={(v) => setParam("cat", v)}>
          <SelectTrigger className="w-full md:w-56 h-11 rounded-xl bg-secondary border-border" aria-label={t("all_categories")}>
            <SelectValue placeholder={t("all_categories")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all_categories")}</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.en}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
          <SelectTrigger className="w-full md:w-52 h-11 rounded-xl bg-secondary border-border" aria-label={t("sort_by")}>
            <SlidersHorizontal className="w-4 h-4 mr-1 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">{t("sort_popular")}</SelectItem>
            <SelectItem value="newest">{t("sort_newest")}</SelectItem>
            <SelectItem value="rating">{t("sort_rating")}</SelectItem>
            <SelectItem value="longest">Longest first</SelectItem>
            <SelectItem value="price_low">{t("sort_price_low")}</SelectItem>
            <SelectItem value="price_high">{t("sort_price_high")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <Select value={length} onValueChange={(v) => setParam("len", v)}>
          <SelectTrigger className="w-auto min-w-[140px] h-10 rounded-full bg-secondary border-border text-sm" aria-label="Length">
            <SelectValue placeholder="Length" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any length</SelectItem>
            <SelectItem value="short">Under 6 hours</SelectItem>
            <SelectItem value="medium">6 – 12 hours</SelectItem>
            <SelectItem value="long">Over 12 hours</SelectItem>
          </SelectContent>
        </Select>
        <Select value={minRating} onValueChange={(v) => setParam("rating", v)}>
          <SelectTrigger className="w-auto min-w-[140px] h-10 rounded-full bg-secondary border-border text-sm" aria-label="Minimum rating">
            <SelectValue placeholder="Rating" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any rating</SelectItem>
            <SelectItem value="4.5">4.5★ &amp; up</SelectItem>
            <SelectItem value="4">4★ &amp; up</SelectItem>
            <SelectItem value="3">3★ &amp; up</SelectItem>
          </SelectContent>
        </Select>

        {chips.map((c) => (
          <button
            key={c.key}
            onClick={() => setParam(c.key, "")}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-brand-500/15 border border-brand-500/30 text-brand-300 text-sm hover:bg-brand-500/25 transition-colors"
          >
            {c.label} <X className="w-3.5 h-3.5" />
          </button>
        ))}

        {clearable && (
          <button
            onClick={clearFilters}
            className="h-10 px-4 rounded-full border border-border text-sm text-muted-foreground hover:text-foreground hover:border-brand-500/50 transition-colors"
          >
            Clear filters
          </button>
        )}

        {header && (
          <span className="ml-auto text-sm text-muted-foreground">
            {filtered.length} {t("results")}
          </span>
        )}
      </div>

      {loading && BOOKS.length === 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-5 gap-y-8">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[2/3] rounded-xl bg-secondary" />
              <div className="h-3 bg-secondary rounded mt-3 w-4/5" />
              <div className="h-2.5 bg-secondary rounded mt-2 w-2/5" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-24 text-center">
          <p className="text-muted-foreground">
            {scope
              ? "Nothing here matches those filters."
              : "No audiobooks match your filters."}
          </p>
          {clearable && (
            <button
              onClick={clearFilters}
              className="mt-4 h-10 px-6 rounded-full bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold text-sm"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-5 gap-y-8">
          {filtered.map((b) => (
            <BookCard key={b.id} book={b} width="w-full" />
          ))}
        </div>
      )}
    </div>
  );
}
